import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Bucket, StoredFile, TransformOperation } from '@storeflare/types';
import { Page, EmptyState } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

/**
 * Visual transform builder. Picks a source file from any active bucket,
 * lets the user assemble an op chain, previews the result against the
 * Transform Worker, and offers a one-click "Save as variant" that calls
 * `/v1/transform/store` so the result lands in R2.
 *
 * Presets are listed in a sidebar; clicking a preset replaces the current
 * op chain with the preset's operations.
 */
interface TransformPreset {
  id: string;
  preset_name: string;
  operations: TransformOperation[];
  created_at: string;
}

const TRANSFORM_BASE =
  import.meta.env['VITE_TRANSFORM_BASE'] ?? '/transform';

const DEFAULT_OPS: TransformOperation[] = [
  { type: 'resize', width: 400, fit: 'cover' },
  { type: 'convert', format: 'webp' },
];

export function TransformPage() {
  const buckets = useQuery({
    queryKey: ['buckets'],
    queryFn: () => api<Bucket[]>('/v1/buckets'),
  });
  const [bucketId, setBucketId] = useState<string | null>(null);
  const activeBucket = bucketId ?? buckets.data?.[0]?.id ?? null;

  const files = useQuery({
    queryKey: ['files', activeBucket],
    enabled: !!activeBucket,
    queryFn: () =>
      api<StoredFile[]>(
        `/v1/files?status=active&limit=50${activeBucket ? `&bucket_id=${activeBucket}` : ''}`,
      ),
  });
  const [sourceId, setSourceId] = useState<string | null>(null);
  const source = sourceId ?? files.data?.find((f) => f.mime_type.startsWith('image/'))?.id ?? null;

  const [ops, setOps] = useState<TransformOperation[]>(DEFAULT_OPS);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const presets = useQuery({
    queryKey: ['presets'],
    queryFn: () => api<TransformPreset[]>('/v1/transform/presets'),
  });

  const preview = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error('No source selected');
      const res = await fetch(`${TRANSFORM_BASE}/v1/transform`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ source, operations: ops }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Transform failed: HTTP ${res.status} ${text}`);
      }
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    onSuccess: (url) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewError(null);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : String(err);
      setPreviewError(message);
      toast.error(message);
    },
  });

  const [variantName, setVariantName] = useState('thumbnail');
  const qc = useQueryClient();
  const saveVariant = useMutation({
    mutationFn: () =>
      api<{ id: string; url: string }>(`${TRANSFORM_BASE}/v1/transform/store`, {
        method: 'POST',
        body: JSON.stringify({
          source,
          operations: ops,
          store: true,
          variant_name: variantName,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['files'] });
      toast.success(`Variant "${variantName}" saved`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save variant');
    },
  });

  const applyPreset = (p: TransformPreset) => {
    setOps(p.operations);
    toast.info(`Applied preset "${p.preset_name}"`);
  };

  if (buckets.isLoading) return <Page title="Transform"><Skeleton className="h-40 w-full" /></Page>;

  return (
    <Page
      title="Transform"
      subtitle="Build an operation chain, preview it, and save the result as a stored variant."
    >
      <div className="grid grid-cols-12 gap-6">
        {/* ---- Source picker + op chain ---- */}
        <div className="col-span-8 space-y-4">
          <Card className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <Label htmlFor="bucket">Bucket</Label>
              <select
                id="bucket"
                value={activeBucket ?? ''}
                onChange={(e) => {
                  setBucketId(e.target.value || null);
                  setSourceId(null);
                }}
                className="rounded-md border bg-background px-3 py-1 text-sm"
              >
                {(buckets.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Source file</Label>
              <div className="mt-1 max-h-48 overflow-auto rounded-md border">
                {files.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : (files.data ?? []).filter((f) => f.mime_type.startsWith('image/')).length === 0 ? (
                  <EmptyState title="No image files in this bucket" />
                ) : (
                  <ul className="divide-y text-sm">
                    {(files.data ?? [])
                      .filter((f) => f.mime_type.startsWith('image/'))
                      .map((f) => (
                        <li
                          key={f.id}
                          className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${
                            source === f.id ? 'bg-muted' : ''
                          }`}
                          onClick={() => setSourceId(f.id)}
                        >
                          <span className="truncate">{f.filename}</span>
                          <span className="text-xs text-muted-foreground">{f.mime_type}</span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          </Card>

          <Card className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <Label>Operations</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setOps([])}>Clear</Button>
                <Button size="sm" disabled={!source || preview.isPending} onClick={() => preview.mutate()}>
                  {preview.isPending ? 'Rendering…' : 'Preview'}
                </Button>
              </div>
            </div>
            <OpChainEditor ops={ops} onChange={setOps} />
            {previewError ? <p className="text-sm text-destructive">{previewError}</p> : null}
          </Card>

          <Card className="flex items-center gap-3 p-4">
            <Label htmlFor="vname" className="shrink-0">Variant name</Label>
            <Input
              id="vname"
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
              placeholder="thumbnail"
              className="max-w-xs"
            />
            <Button
              disabled={!source || !variantName || saveVariant.isPending}
              onClick={() => saveVariant.mutate()}
            >
              {saveVariant.isPending ? 'Saving…' : 'Save as variant'}
            </Button>
            {saveVariant.isSuccess ? (
              <span className="text-sm text-muted-foreground">Saved.</span>
            ) : null}
          </Card>
        </div>

        {/* ---- Preview + presets ---- */}
        <div className="col-span-4 space-y-4">
          <Card className="p-4">
            <Label>Preview</Label>
            <div className="mt-2 flex aspect-square items-center justify-center overflow-hidden rounded-md border bg-muted">
              {previewUrl ? (
                <img src={previewUrl} alt="preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">No preview yet</span>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <Label>Presets</Label>
            <ul className="mt-2 space-y-1 text-sm">
              {presets.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : (presets.data ?? []).length === 0 ? (
                <li className="text-xs text-muted-foreground">No saved presets yet.</li>
              ) : (
                (presets.data ?? []).map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{p.preset_name}</span>
                    <Button size="sm" variant="outline" onClick={() => applyPreset(p)}>Use</Button>
                  </li>
                ))
              )}
            </ul>
          </Card>
        </div>
      </div>
    </Page>
  );
}

// ---------- Op chain editor ----------

const OP_TYPES = ['resize', 'crop', 'rotate', 'flip', 'convert', 'quality', 'filter', 'watermark'] as const;

function OpChainEditor({
  ops,
  onChange,
}: {
  ops: TransformOperation[];
  onChange: (next: TransformOperation[]) => void;
}) {
  const update = (i: number, next: TransformOperation) =>
    onChange(ops.map((o, idx) => (idx === i ? next : o)));
  const remove = (i: number) => onChange(ops.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= ops.length) return;
    const next = ops.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const add = (type: typeof OP_TYPES[number]) => onChange([...ops, defaultOp(type)]);

  return (
    <div className="space-y-2">
      {ops.length === 0 ? (
        <p className="text-sm text-muted-foreground">No operations. Add one below.</p>
      ) : (
        <ol className="space-y-2">
          {ops.map((op, i) => (
            <li key={i} className="flex items-start gap-2 rounded-md border p-2">
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} className="text-xs">▲</button>
                <button type="button" onClick={() => move(i, 1)} className="text-xs">▼</button>
              </div>
              <OpEditor op={op} onChange={(next) => update(i, next)} />
              <Button size="sm" variant="ghost" onClick={() => remove(i)}>Remove</Button>
            </li>
          ))}
        </ol>
      )}
      <div className="flex flex-wrap gap-1 pt-1">
        {OP_TYPES.map((t) => (
          <Button key={t} size="sm" variant="outline" onClick={() => add(t)}>+ {t}</Button>
        ))}
      </div>
    </div>
  );
}

function OpEditor({ op, onChange }: { op: TransformOperation; onChange: (next: TransformOperation) => void }) {
  const num = (v: string) => (v === '' ? undefined : Number(v));
  switch (op.type) {
    case 'resize':
      return (
        <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
          <strong className="text-xs uppercase">resize</strong>
          <Field label="w">
            <Input className="w-20" value={op.width ?? ''} onChange={(e) => onChange({ ...op, width: num(e.target.value) })} />
          </Field>
          <Field label="h">
            <Input className="w-20" value={op.height ?? ''} onChange={(e) => onChange({ ...op, height: num(e.target.value) })} />
          </Field>
          <Field label="fit">
            <select value={op.fit ?? 'cover'} onChange={(e) => onChange({ ...op, fit: e.target.value as typeof op.fit })} className="rounded-md border bg-background px-2 py-1">
              {['cover', 'contain', 'fill', 'inside', 'outside'].map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
        </div>
      );
    case 'crop':
      return (
        <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
          <strong className="text-xs uppercase">crop</strong>
          <Field label="x"><Input className="w-16" value={op.x} onChange={(e) => onChange({ ...op, x: Number(e.target.value) })} /></Field>
          <Field label="y"><Input className="w-16" value={op.y} onChange={(e) => onChange({ ...op, y: Number(e.target.value) })} /></Field>
          <Field label="w"><Input className="w-16" value={op.width} onChange={(e) => onChange({ ...op, width: Number(e.target.value) })} /></Field>
          <Field label="h"><Input className="w-16" value={op.height} onChange={(e) => onChange({ ...op, height: Number(e.target.value) })} /></Field>
        </div>
      );
    case 'rotate':
      return (
        <Field label="rotate °">
          <Input className="w-20" value={op.degrees} onChange={(e) => onChange({ ...op, degrees: Number(e.target.value) })} />
        </Field>
      );
    case 'flip':
      return (
        <Field label="flip">
          <select value={op.direction} onChange={(e) => onChange({ ...op, direction: e.target.value as 'horizontal' | 'vertical' })} className="rounded-md border bg-background px-2 py-1">
            <option value="horizontal">horizontal</option>
            <option value="vertical">vertical</option>
          </select>
        </Field>
      );
    case 'convert':
      return (
        <Field label="format">
          <select value={op.format} onChange={(e) => onChange({ ...op, format: e.target.value as 'webp' | 'jpeg' | 'png' | 'avif' })} className="rounded-md border bg-background px-2 py-1">
            {['webp', 'jpeg', 'png', 'avif'].map((f) => <option key={f}>{f}</option>)}
          </select>
        </Field>
      );
    case 'quality':
      return (
        <Field label="quality">
          <Input className="w-20" value={op.value} onChange={(e) => onChange({ ...op, value: Number(e.target.value) })} />
        </Field>
      );
    case 'filter':
      return (
        <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
          <strong className="text-xs uppercase">filter</strong>
          <Field label="name">
            <select value={op.name} onChange={(e) => onChange({ ...op, name: e.target.value as 'grayscale' | 'sepia' | 'blur' | 'sharpen' })} className="rounded-md border bg-background px-2 py-1">
              {['grayscale', 'sepia', 'blur', 'sharpen'].map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="intensity">
            <Input className="w-20" value={op.intensity ?? ''} onChange={(e) => onChange({ ...op, intensity: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
        </div>
      );
    case 'watermark':
      return (
        <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
          <strong className="text-xs uppercase">watermark</strong>
          <Field label="text"><Input className="w-40" value={op.text} onChange={(e) => onChange({ ...op, text: e.target.value })} /></Field>
          <Field label="position">
            <select value={op.position ?? 'bottom-right'} onChange={(e) => onChange({ ...op, position: e.target.value as typeof op.position })} className="rounded-md border bg-background px-2 py-1">
              {['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="opacity">
            <Input className="w-20" value={op.opacity ?? ''} onChange={(e) => onChange({ ...op, opacity: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
        </div>
      );
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </span>
  );
}

function defaultOp(t: typeof OP_TYPES[number]): TransformOperation {
  switch (t) {
    case 'resize': return { type: 'resize', width: 400, fit: 'cover' };
    case 'crop': return { type: 'crop', x: 0, y: 0, width: 200, height: 200 };
    case 'rotate': return { type: 'rotate', degrees: 90 };
    case 'flip': return { type: 'flip', direction: 'horizontal' };
    case 'convert': return { type: 'convert', format: 'webp' };
    case 'quality': return { type: 'quality', value: 85 };
    case 'filter': return { type: 'filter', name: 'grayscale' };
    case 'watermark': return { type: 'watermark', text: '© Acme', position: 'bottom-right', opacity: 0.3 };
  }
}
